// src/pages/vendor/Pickups/components/PickupCard.tsx

import React, { useState } from 'react';
import {
    Package, MapPin, Calendar, Clock, User, Phone,
    Eye, X, Check, ChevronDown, ChevronUp,
    Building2, ShoppingBag,
    Trash2,
    Play
} from 'lucide-react';
import PickupStatusBadge from '../vendor/PickupStatusBadge';
import { type Pickup } from '../../types';
import { formatDateEnglish, formatDateTimeEnglish } from '../../utils/date';

interface PickupCardProps {
    pickup: Pickup;
    hasMultipleStores: boolean;
    onViewDetails: (id: number) => void;
    onCancel: (id: number) => void;
    onDelete: (id: number) => void;
    onContinue: (id: number) => void;
}

const PickupCard: React.FC<PickupCardProps> = ({
    pickup,
    hasMultipleStores,
    onViewDetails,
    onDelete,
    onCancel,
    onContinue,
}) => {
    const [expanded, setExpanded] = useState(false);

    const isDraft = pickup.status === 'draft';
    const isPending = pickup.status === 'pending';
    const isInTransit = pickup.status === 'in_transit';
    const canCancel = isDraft || isPending;
    const canDelete = pickup.status === 'draft' || pickup.status === 'cancelled';

    const storeLabel = pickup.store_names?.length
        ? pickup.store_names[0] + (pickup.store_names.length > 1 ? ` +${pickup.store_names.length - 1}` : '')
        : '—';

    const formatDate = (d?: string | null) => {
        if (!d) return null;
        return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const formatTime = (t?: string | null) => {
        if (!t) return null;
        const [h, m] = t.split(':');
        const hour = parseInt(h, 10);
        const suffix = hour >= 12 ? 'PM' : 'AM';
        const display = hour % 12 || 12;
        return `${display}:${m} ${suffix}`;
    };
    
    return (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
            {/* Header */}
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                    <Package className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <span className="text-sm font-medium text-gray-900 truncate">PU-{pickup.id}</span>
                    <span className="text-xs text-gray-400">·</span>
                    <span className="text-xs text-gray-400">
                        {formatDateTimeEnglish(pickup.created_at)}
                    </span>
                </div>
                <PickupStatusBadge status={pickup.status} size="sm" />
            </div>

            {/* Body */}
            <div className="p-4">
                <div className="space-y-2">
                    <div className="flex items-start gap-2">
                        {hasMultipleStores ? (
                            <div className="flex items-start gap-2">
                                <Building2 className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate" title={pickup.store_names?.join(', ')}>
                                        {storeLabel}
                                    </p>
                                    <div className="flex items-start gap-1 mt-0.5">
                                        <MapPin className="h-3.5 w-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                                        <p className="text-sm text-gray-600 truncate">
                                            {[pickup.pickup_address_line_1, pickup.pickup_city_name].filter(Boolean).join(', ')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-start gap-2">
                                <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-gray-600 truncate">
                                    {[pickup.pickup_address_line_1, pickup.pickup_city_name].filter(Boolean).join(', ')}
                                </p>
                            </div>
                        )}
                    </div>
                    {/* Date & Time */}
                    {(pickup.pickup_date || pickup.available_from) && (
                        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                            {pickup.pickup_date && (
                                <div className="flex items-center gap-1">
                                    <Calendar className="h-3.5 w-3.5 text-gray-400" />
                                    <span>{formatDate(pickup.pickup_date)}</span>
                                </div>
                            )}
                            {pickup.available_from && (
                                <div className="flex items-center gap-1">
                                    <Clock className="h-3.5 w-3.5 text-gray-400" />
                                    <span>From {formatTime(pickup.available_from)}</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Items & Boxes */}
                    <div className="flex items-center gap-3 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                            <ShoppingBag className="h-3.5 w-3.5" />
                            {pickup.items_count} item{pickup.items_count !== 1 ? 's' : ''}
                        </span>
                        {pickup.variant_count > 0 && (
                            <>
                                <span>•</span>
                                <span>{pickup.variant_count} variant{pickup.variant_count !== 1 ? 's' : ''}</span>
                            </>
                        )}
                        {pickup.box_count > 0 && (
                            <>
                                <span>•</span>
                                <span>{pickup.box_count} box{pickup.box_count !== 1 ? 'es' : ''}</span>
                            </>
                        )}
                    </div>

                    {/* Driver Info */}
                    {pickup.driver_name && (
                        <div className="mt-2 pt-2 border-t border-gray-100">
                            <div className="flex flex-wrap items-center gap-3 text-sm">
                                <div className="flex items-center gap-1.5 text-gray-600">
                                    <User className="h-3.5 w-3.5 text-gray-400" />
                                    <span>{pickup.driver_name}</span>
                                </div>
                                {pickup.driver_phone && (
                                    <div className="flex items-center gap-1.5 text-gray-600">
                                        <Phone className="h-3.5 w-3.5 text-gray-400" />
                                        <span>{pickup.driver_phone}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="mt-4 pt-3 border-t border-gray-100">
                    <div className="sm:hidden flex items-center justify-between">
                        <button
                            onClick={() => setExpanded(!expanded)}
                            className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                        >
                            {expanded ? (
                                <>Hide actions <ChevronUp className="h-3.5 w-3.5" /></>
                            ) : (
                                <>Show actions <ChevronDown className="h-3.5 w-3.5" /></>
                            )}
                        </button>
                    </div>

                    <div className={`${expanded ? 'flex' : 'hidden sm:flex'} items-center justify-between gap-2`}>
                        {/* Left: primary action */}
                        <div className="flex items-center gap-2">
                            {isDraft ? (
                                <button
                                    onClick={() => onContinue(pickup.id)}
                                    className="px-3 py-1.5 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-1.5"
                                >
                                    <Play className="h-3.5 w-3.5" />
                                    Continue
                                </button>
                            ) : (
                                <button
                                    onClick={() => onViewDetails(pickup.id)}
                                    className="px-3 py-1.5 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center gap-1.5"
                                >
                                    <Eye className="h-3.5 w-3.5" />
                                    View Details
                                </button>
                            )}

                            {isInTransit && (
                                <span className="text-xs text-gray-500 italic">Awaiting warehouse receipt</span>
                            )}
                        </div>

                        {/* Right: destructive action, isolated */}
                        {canDelete && (
                            <button
                                onClick={() => onDelete(pickup.id)}
                                className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-1.5"
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                                Delete
                            </button>
                        )}

                        {canCancel && !isDraft && (
                            <button
                                onClick={() => onCancel(pickup.id)}
                                className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-1.5"
                            >
                                <X className="h-3.5 w-3.5" />
                                Cancel
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PickupCard;